import type { PurposeOfUse } from './types/common';
import type { SessionUser } from './types/auth';

/**
 * In-memory credential store for the API client.
 * The access token is never written to storage; a page reload restores the session through
 * POST /auth/refresh (httpOnly refresh cookie). Only non-secret context (tenant, location) is persisted.
 */
export interface SessionSnapshot {
  accessToken: string | null;
  accessExpiresAt: number | null;
  user: SessionUser | null;
  tenantId: string | null;
  locationId: string | null;
  purpose: PurposeOfUse;
  stepUpToken: string | null;
  stepUpExpiresAt: number | null;
}

type Listener = (s: SessionSnapshot, reason?: string) => void;

const CTX_KEY = 'etnas.ctx';

function loadCtx(): Pick<SessionSnapshot, 'tenantId' | 'locationId'> {
  try {
    const raw = globalThis.sessionStorage?.getItem(CTX_KEY);
    if (raw) {
      const v = JSON.parse(raw) as { tenantId?: string | null; locationId?: string | null };
      return { tenantId: v.tenantId ?? null, locationId: v.locationId ?? null };
    }
  } catch {
    /* storage unavailable */
  }
  return { tenantId: null, locationId: null };
}

class SessionStore {
  private s: SessionSnapshot = {
    accessToken: null,
    accessExpiresAt: null,
    user: null,
    purpose: 'TREATMENT',
    stepUpToken: null,
    stepUpExpiresAt: null,
    ...loadCtx(),
  };
  private listeners = new Set<Listener>();

  get(): SessionSnapshot {
    return this.s;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private set(patch: Partial<SessionSnapshot>, reason?: string) {
    this.s = { ...this.s, ...patch };
    try {
      globalThis.sessionStorage?.setItem(CTX_KEY, JSON.stringify({ tenantId: this.s.tenantId, locationId: this.s.locationId }));
    } catch {
      /* ignore */
    }
    this.listeners.forEach((l) => l(this.s, reason));
  }

  setTokens(accessToken: string, expiresInSeconds: number, user?: SessionUser, opts: { keepContext?: boolean } = {}) {
    // A refresh (keepContext) never replaces the active tenant (R-110). A different person starts from their own
    // explicit tenant (or none until GET /me says which).
    const switching = !!user && !!this.s.user && user.id !== this.s.user.id;
    const tenantId = opts.keepContext ? this.s.tenantId : switching ? user!.tenantId ?? null : user?.tenantId ?? this.s.tenantId;
    this.set({
      accessToken,
      accessExpiresAt: Date.now() + expiresInSeconds * 1000,
      ...(user ? { user, tenantId } : {}),
    }, 'tokens');
  }

  /** Hydrate the verified /me identity after cookie restoration, without replacing tokens or context. */
  setUser(user: SessionUser) {
    this.set({ user }, 'identity');
  }

  setContext(patch: Partial<Pick<SessionSnapshot, 'tenantId' | 'locationId' | 'purpose'>>) {
    this.set(patch, 'context');
  }

  setStepUp(token: string, expiresInSeconds: number) {
    this.set({ stepUpToken: token, stepUpExpiresAt: Date.now() + expiresInSeconds * 1000 }, 'step-up');
  }

  /** A step-up token is single-session but time-boxed (300 s). */
  validStepUp(): string | null {
    const { stepUpToken, stepUpExpiresAt } = this.s;
    if (stepUpToken && stepUpExpiresAt && stepUpExpiresAt - 5_000 > Date.now()) return stepUpToken;
    return null;
  }

  clearStepUp() {
    this.set({ stepUpToken: null, stepUpExpiresAt: null }, 'step-up-cleared');
  }

  clear(reason: 'logout' | 'expired' | 'revoked' | 'identity-changed' = 'logout') {
    // Expiry keeps the tenant (the same person resumes); logout forgets it.
    const ctx = reason === 'logout' || reason === 'identity-changed' ? { tenantId: null, locationId: null } : {};
    this.set({ accessToken: null, accessExpiresAt: null, user: reason === 'expired' ? this.s.user : null, stepUpToken: null, stepUpExpiresAt: null, ...ctx }, reason);
  }

  isAuthenticated(): boolean {
    return !!this.s.accessToken;
  }
}

export const session = new SessionStore();
